import CustomModalWrapper from 'components/CustomModalWrapper/CustomModalWrapper';
import DetailTable from 'components/DetailTable';
import { ConfigItem } from 'components/DetailTable/types';
import LoadingScreen from 'components/LoadingScreen';
import { useAtomValue } from 'jotai';
import { Fragment, useEffect, useState } from 'react';
import { Row, Col, Button, ButtonGroup } from 'reactstrap';
import { companyIdAtom } from 'views/state';

import PCFindingSummaryCard from '../PCFindingSummaryCard';
import { fetchPostureDetail, processJsonData } from './helper';

interface Props {
  id: number;
}

const PCFindingDetailModalContent = ({ id }: Props) => {
  const [activeTab, setActiveTab] = useState(0);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const companyId = useAtomValue(companyIdAtom);

  useEffect(() => {
    fetchPostureDetail(id, companyId)
      .then((res) => {
        if (res) {
          const processedData = processJsonData(res);
          setData(processedData);
        }
      })
      .catch((err) => {
        throw err;
      })
      .finally(() => setIsLoading(false));
  }, [id]);

  const postureSummaryTabConfig: ConfigItem[] = [
    {
      name: 'Policy',
      key: 'policy',
      type: 'object',
      config: [
        { name: 'Display Name', key: 'policy_display_name', type: 'string' },
        { name: 'Last Evaluation', key: 'evaluation_date', type: 'date' },
      ],
    },
    { name: 'Control Statement', key: 'control_statement', type: 'string' },
    {
      name: 'Instance Details',
      key: 'instance_details',
      type: 'object',
      config: [
        { name: 'Instance', key: 'instance', type: 'object' },
        { name: 'Technology', key: 'technology_name', type: 'string' },
      ],
    },
    {
      name: 'Host',
      key: 'host',
      type: 'object',
      config: [
        { name: 'ID', key: 'host_id', type: 'string' },
        { name: 'Name', key: 'host_name', type: 'string' },
        { name: 'Alias', key: 'host_alias', type: 'string' },
        { name: 'IP', key: 'host_ip', type: 'string' },
      ],
    },
    { name: 'First Pass Date', key: 'first_pass_date', type: 'date' },
    { name: 'Last Pass Date', key: 'last_pass_date', type: 'date' },
    { name: 'First Fail Date', key: 'first_fail_date', type: 'date' },
    { name: 'Last Fail Date', key: 'last_fail_date', type: 'date' },
    { name: 'Age', key: 'age', type: 'string' },
    { name: 'Finding Modified Date', key: 'modified_date', type: 'date' },
  ];

  const generalTabConfig: ConfigItem[] = [
    {
      name: 'Control Details',
      key: 'control',
      type: 'object',
      config: [
        { name: 'Category', key: 'control_category', type: 'string' },
        {
          name: 'Sub Category',
          key: 'control_sub_category',
          type: 'string',
        },
        {
          name: 'Ignore Error',
          key: 'control_ignore_error',
          type: 'boolean',
        },
        {
          name: 'Deprecated',
          key: 'control_deprecated',
          type: 'boolean',
        },
        {
          name: 'Deprecated Date',
          key: 'control_deprecated_date',
          type: 'date',
        },
        {
          name: 'Modified Date',
          key: 'control_modified_date',
          type: 'date',
        },
      ],
    },
    { name: 'Technology', key: 'technology_name', type: 'string' },
    {
      name: 'Technology Data',
      key: 'control_technology_data',
      type: 'object',
      config: [
        { name: 'Rationale', key: 'rationale', type: 'string' },
        {
          name: 'Datapoints',
          key: 'datapoint',
          type: 'list',
          listConfig: {
            config: [
              { name: 'CARDINALITY', key: 'CARDINALITY', type: 'string' },
              { name: 'DEFAULT_VALUES', key: 'DEFAULT_VALUES', type: 'string' },
              { name: 'OPERATOR', key: 'OPERATOR', type: 'string' },
            ],
          },
        },
        { name: 'Use Scan Value', key: 'use_scan_value', type: 'string' },
        { name: 'DB Query', key: 'db_query', type: 'string' },
        { name: 'Description', key: 'description', type: 'string' },
      ],
    },
    { name: 'Remediation', key: 'remediation', type: 'string' },
    {
      name: 'Evidence',
      key: 'evidence.decoded_evidence',
      type: 'object',
      config: [
        { name: 'Description', key: 'Description', type: 'html' },
        {
          name: 'Expected',
          key: 'Expected',
          type: 'object',
          config: [
            { name: 'Regex', key: 'Regex', type: 'string' },
            { name: 'FixedValues', key: 'FixedValues', type: 'array' },
          ],
        },
        {
          name: 'Actual',
          key: 'Actual',
          type: 'object',
          config: [
            { name: 'LastUpdated', key: 'LastUpdated', type: 'date' },
            { name: 'Value', key: 'Value', type: 'string' },
          ],
        },
      ],
    },
    { name: 'Exception', key: 'exception', type: 'string' },
    {
      name: 'Cause of Failure',
      key: 'cause_of_failure',
      type: 'object',
      config: [
        { name: 'Directory FIM UDC', key: 'DIRECTORY_FIM_UDC', type: 'string' },
        {
          name: 'Unexpected',
          key: 'UNEXPECTED',
          type: 'object',
          config: [{ name: 'Value', key: 'V', type: 'string' }],
        },
        {
          name: 'Missing',
          key: 'MISSING',
          type: 'object',
          config: [{ name: 'Value', key: 'V', type: 'string' }],
        },
      ],
    },
  ];

  if (isLoading && !data) return <LoadingScreen />;

  const tabs = [
    {
      title: 'Finding Summary',
      config: postureSummaryTabConfig,
      data: data,
    },

    {
      title: 'General Information',
      config: generalTabConfig,
      data: data,
    },
  ];

  if (!data) return <div>No data available</div>;

  return (
    <CustomModalWrapper id="pc-finding-scroll">
      <Fragment>
        <Row>
          <Col sm="6" md="4" lg="3">
            <ButtonGroup
              vertical
              className="btn-group-toggle"
              style={{ maxWidth: '100%', width: '100%' }}
            >
              {tabs.map((tab, index) => (
                <Button
                  key={`tab-btn-${index}`}
                  tag="label"
                  style={{
                    padding: '10px',
                    margin: '0px',
                    textAlign: 'left',
                  }}
                  color={activeTab === index ? 'primary' : 'secondary'}
                  size="sm"
                  title={tab.title}
                  onClick={() => setActiveTab(index)}
                >
                  {tab.title}
                </Button>
              ))}
            </ButtonGroup>
          </Col>
          <Col sm="6" md="8" lg="9">
            <CustomModalWrapper id="scroll-wrapper">
              {tabs.map(
                (tab, index) =>
                  activeTab === index && (
                    <>
                      <Row>
                        <Col sm="12">
                          {data ? (
                            <PCFindingSummaryCard rowData={data} />
                          ) : (
                            <></>
                          )}
                        </Col>
                      </Row>
                      <DetailTable
                        key={index}
                        data={tab.data}
                        config={tab.config as ConfigItem[]}
                      />
                    </>
                  ),
              )}
            </CustomModalWrapper>
          </Col>
        </Row>
      </Fragment>
    </CustomModalWrapper>
  );
};

export default PCFindingDetailModalContent;
